/**
 * Push notification groundwork (#37 — IOS-4.4).
 *
 * Registers the device for APNs / FCM, captures the device token, stores it
 * in Preferences (Keychain on iOS), and POSTs it to Foroba's device-token
 * endpoint so the backend can target this device in future.
 *
 * IMPORTANT: Actual push delivery requires the Apple Developer Program ($99)
 * and an APNs certificate/key. This module ships the client-side groundwork:
 * - Token capture on registration success
 * - Silent error handling on registration failure (e.g. Simulator, no ADP)
 * - Stub handlers for received notifications (no-op until ADP is available)
 *
 * On web/PWA: entire module is a no-op (Capacitor.isNativePlatform() guard).
 */

import { Capacitor } from '@capacitor/core'
import { PushNotifications, type Token } from '@capacitor/push-notifications'
import { Preferences } from '@capacitor/preferences'

const PUSH_TOKEN_KEY = 'pfh_push_token'
const PLATFORM = 'ios'

/** Store push token in Preferences so it survives re-registration. */
async function savePushToken(token: string): Promise<void> {
  await Preferences.set({ key: PUSH_TOKEN_KEY, value: token })
}

/** Send the token to the Foroba backend for server-side push delivery later. */
async function reportTokenToBackend(token: string): Promise<void> {
  try {
    await fetch('/api/v1/foroba/auth/device/push-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform: PLATFORM }),
    })
  } catch {
    // Network failure — token is stored locally; will retry on next launch.
  }
}

/**
 * Register for push notifications. Call once from bootstrap() on native.
 *
 * Flow:
 *   1. Request permission (iOS shows the "Allow notifications?" dialog).
 *   2. On permission granted: call register() to ask APNs for a token.
 *   3. 'registration' event: token received → store + report to backend.
 *   4. 'registrationError' event: swallowed silently (Simulator / no ADP).
 *   5. 'pushNotificationReceived': foreground notification — stub handler.
 *   6. 'pushNotificationActionPerformed': user tapped — stub handler.
 */
export async function registerPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  // Register listeners BEFORE requesting permission so events are not missed.
  await PushNotifications.addListener('registration', async (token: Token) => {
    await savePushToken(token.value)
    await reportTokenToBackend(token.value)
  })

  await PushNotifications.addListener('registrationError', (err) => {
    // Silently absorb — expected on Simulator and devices without ADP provisioning.
    console.debug('[push] registration error (expected without ADP):', err)
  })

  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    // Foreground notification received — stub. Will be wired in IOS-5+ once
    // ADP is available and real pushes can be sent.
    console.debug('[push] notification received:', notification.title)
  })

  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    // User tapped a notification — stub. Deep-link routing added in IOS-5+.
    console.debug('[push] action performed:', action.notification.title)
  })

  // Request permission
  const result = await PushNotifications.requestPermissions()
  if (result.receive === 'granted') {
    await PushNotifications.register()
  }
  // If denied: no error, no retry — user can enable later in Settings.
}
